'use client'

import {
  personDisplayName,
  useProfileById,
  usePersonMatchDetailTab,
  buildRecruiterPersonMatchBackPath,
} from '@yeshuman/sdk'
import type { RecruiterPersonMatchOrigin } from '@yeshuman/sdk'
import { useMemo } from 'react'
import { useLocation, useParams, useSearchParams } from 'react-router-dom'
import { useBrief } from './use-briefs'
import type { RecruiterPersonMatchLocationState } from './use-navigate-to-recruiter-person'

export function useRecruiterPersonMatchState() {
  const { profileId = '', briefId: briefIdParam } = useParams<{
    profileId: string
    briefId?: string
  }>()
  const location = useLocation()
  const personState = (location.state as RecruiterPersonMatchLocationState | null) ?? {}
  const [searchParams] = useSearchParams()
  const evaluationIdFromQuery = searchParams.get('evaluation')

  const briefId = briefIdParam ?? personState.briefId
  const origin: RecruiterPersonMatchOrigin =
    personState.origin ?? (briefId ? 'brief' : 'talent')

  const { brief, isLoading: briefLoading } = useBrief(briefId || null)
  const { profile, isLoading: profileLoading, error: profileError } = useProfileById(
    profileId || null,
  )

  const displayName = useMemo(() => {
    if (personState.label?.trim()) return personState.label.trim()
    if (profile) return personDisplayName(profile)
    return 'Candidate'
  }, [personState.label, profile])

  const backPath =
    personState.backPath ?? buildRecruiterPersonMatchBackPath(origin, briefId)

  const breadcrumbs = useMemo(() => {
    if (origin === 'brief' && briefId) {
      return [
        { label: 'Briefs', to: '/recruiter/briefs' },
        {
          label: brief?.title ?? 'Brief',
          to: `/recruiter/briefs/${briefId}?tab=search`,
        },
        { label: displayName },
      ]
    }
    return [
      { label: 'Talent', to: '/recruiter/talent?tab=search' },
      { label: displayName },
    ]
  }, [brief?.title, briefId, displayName, origin])

  const evaluationId = evaluationIdFromQuery ?? undefined
  const { activeTab, setActiveTab } = usePersonMatchDetailTab(evaluationId)

  const isLoading = profileLoading || (origin === 'brief' && briefId ? briefLoading : false)
  const missingRequired = !profileId
  const missingData = profileError != null || !profile

  return {
    profileId,
    briefId,
    origin,
    profile,
    displayName,
    breadcrumbs,
    backPath,
    evaluationId,
    activeTab,
    setActiveTab,
    hasAnalysis: Boolean(evaluationId),
    isLoading,
    missingRequired,
    missingData,
  }
}
